import { type Currency, formatAmount } from "@loot-os/api-contract";
import type {
  StaffBlockWire,
  StaffUserLiveBreakEntryWire,
  StaffUserLiveBreaksWire,
} from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import { Badge } from "@loot-os/ui/components/badge";
import { buttonVariants } from "@loot-os/ui/components/button";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { amountColumnHeader, amountInputValue, useCurrency } from "../lib/currency";
import {
  LIVE_BREAK_RELEASE_REASON_LABELS,
  LIVE_BREAK_STATE_BADGE,
  LIVE_BREAK_STATE_LABELS,
  liveBreakState,
  staffEndUserLabel,
} from "../lib/liveBreakStaff";
import { ratePercentText } from "../lib/liveBreaks";
import { useTRPC } from "../lib/trpc";
import { ErrorAlert } from "./ErrorAlert";
import { LoadMoreButton } from "./LoadMoreButton";
import { ShortDate } from "./ShortDate";

type EntryRow = StaffUserLiveBreakEntryWire;

const entryColumns = createDataTableColumns<EntryRow>();

function buildEntryColumns(tenantId: string, currency: Currency) {
  const money = (value: unknown) => amountInputValue(Number(value), currency);
  return entryColumns.columns([
    entryColumns.accessor((entry) => entry.liveBreak.title, {
      id: "liveBreak",
      header: "Live break",
      size: 220,
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/storefront/live-breaks/list/$liveBreakId"
          params={{ tenantId, liveBreakId: row.original.liveBreak.id }}
          className="block truncate hover:underline"
          title={getValue()}
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    entryColumns.accessor(
      (entry) => (entry.liveBreak.host ? staffEndUserLabel(entry.liveBreak.host) : "House"),
      {
        id: "host",
        header: "Host",
        meta: { filter: "text" },
      },
    ),
    entryColumns.accessor("boxName", {
      header: "Box",
      size: 180,
      cell: ({ getValue }) => <span className="block truncate">{getValue()}</span>,
      meta: { filter: "text" },
    }),
    entryColumns.accessor("amount", {
      header: "Boxes",
      size: 80,
      meta: { align: "end", filter: "number" },
    }),
    entryColumns.accessor("holdCents", {
      header: amountColumnHeader("Total", currency),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
      ),
      meta: { align: "end", filter: "number", filterCents: true, csv: money },
    }),
    entryColumns.accessor(
      (entry) =>
        entry.releaseReason
          ? LIVE_BREAK_RELEASE_REASON_LABELS[entry.releaseReason]
          : entry.status === "opened"
            ? entry.openGroup?.status === "claimed"
              ? "opened · claimed"
              : "opened · unclaimed"
            : entry.position === null
              ? "queued"
              : `queued #${entry.position}`,
      {
        id: "status",
        header: "Status",
        cell: ({ getValue, row }) =>
          row.original.status === "queued" ? (
            <Badge>{getValue()}</Badge>
          ) : (
            <Badge variant={row.original.status === "opened" ? "secondary" : "outline"}>
              {getValue()}
            </Badge>
          ),
        meta: { filter: "text" },
      },
    ),
    entryColumns.accessor("createdAt", {
      header: "Bought",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
  ]);
}

/** One block line: who keeps whom out, by whom, when. */
function BlockLine({
  tenantId,
  block,
  side,
}: {
  tenantId: string;
  block: StaffBlockWire;
  side: "against" | "made";
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 py-1.5 text-sm">
      {side === "against" ? (
        block.host ? (
          <Link
            to="/tenants/$tenantId/storefront/live-breaks/hosts/$userId"
            params={{ tenantId, userId: block.host.endUserId }}
            className="hover:underline"
          >
            {`${staffEndUserLabel(block.host)}'s live breaks`}
          </Link>
        ) : (
          <span>House live breaks</span>
        )
      ) : (
        <Link
          to="/tenants/$tenantId/users/$userId"
          params={{ tenantId, userId: block.endUser.endUserId }}
          className="hover:underline"
        >
          {staffEndUserLabel(block.endUser)}
        </Link>
      )}
      <span className="text-xs text-muted-foreground">
        {block.blockedBy === "host" ? "by the host" : "by staff"} ·{" "}
        <ShortDate value={block.createdAt} />
      </span>
    </li>
  );
}

/** Their standing as a host and their Twitch link, above the entries. */
function HostSummary({
  tenantId,
  endUserId,
  data,
}: {
  tenantId: string;
  endUserId: string;
  data: StaffUserLiveBreaksWire;
}) {
  const currency = useCurrency();
  const { host, twitch } = data;
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
      <div className="grid gap-0.5">
        <dt className="text-xs text-muted-foreground">Host</dt>
        <dd>
          {host === null ? (
            <span className="text-muted-foreground">Never hosted</span>
          ) : (
            <Link
              to="/tenants/$tenantId/storefront/live-breaks/hosts/$userId"
              params={{ tenantId, userId: endUserId }}
              className="hover:underline"
            >
              {`${formatNumber(host.totals.liveBreaksHosted)} ${host.totals.liveBreaksHosted === 1 ? "live break" : "live breaks"} · ${ratePercentText(host.terms.rateBps)}%`}
            </Link>
          )}
        </dd>
      </div>
      <div className="grid gap-0.5">
        <dt className="text-xs text-muted-foreground">Host Allow List</dt>
        <dd>{host?.allowedAt ? <ShortDate value={host.allowedAt} /> : "Not on it"}</dd>
      </div>
      <div className="grid gap-0.5">
        <dt className="text-xs text-muted-foreground">Commission unclaimed · claimed</dt>
        <dd className="font-mono text-xs leading-5">
          {host
            ? `${formatAmount(host.commission.unclaimedCents, currency)} · ${formatAmount(host.commission.claimedCents, currency)}`
            : "—"}
        </dd>
      </div>
      <div className="grid gap-0.5">
        <dt className="text-xs text-muted-foreground">Twitch</dt>
        <dd>
          {twitch ? (
            <a
              href={`https://www.twitch.tv/${twitch.login}`}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {twitch.login}
            </a>
          ) : (
            <span className="text-muted-foreground">Not linked</span>
          )}
        </dd>
      </div>
    </dl>
  );
}

/**
 * A customer's live breaks (`users.liveBreaks`): their host standing and Twitch link (login only),
 * every entry they bought, the live breaks they hosted, and the Blocks on either side of them.
 */
export function UserLiveBreaksTab({ endUserId }: { endUserId: string }) {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const trpc = useTRPC();
  const currency = useCurrency();
  const query = useInfiniteQuery(
    trpc.users.liveBreaks.infiniteQueryOptions(
      { endUserId, limit: 25 },
      { getNextPageParam: (last) => last.entries.nextCursor },
    ),
  );
  const first = query.data?.pages[0];
  const entries = useMemo(
    () => query.data?.pages.flatMap((page) => page.entries.items) ?? [],
    [query.data],
  );
  const columns = useMemo(() => buildEntryColumns(tenantId, currency), [tenantId, currency]);

  if (query.error) return <ErrorAlert message={query.error.message} />;
  if (!first) return <Skeleton className="h-40" />;
  const hosted = first.hostedLiveBreaks;

  return (
    <div className="flex flex-col gap-6 py-2">
      <HostSummary tenantId={tenantId} endUserId={endUserId} data={first} />

      <section className="flex min-w-0 flex-col gap-2">
        <h3 className="text-sm font-medium">Entries</h3>
        <DataTable
          columns={columns}
          data={entries}
          getRowId={(entry) => entry.id}
          search="Search entries"
          csvName={`user-${endUserId}-live-break-entries`}
          emptyMessage="They haven't bought into a live break."
        />
        <LoadMoreButton
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        />
      </section>

      {hosted.liveBreaks.length > 0 && (
        <section className="flex min-w-0 flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium">Hosted</h3>
            <Link
              to="/tenants/$tenantId/storefront/live-breaks/list"
              params={{ tenantId }}
              search={{ host: endUserId }}
              className={buttonVariants({ variant: "outline", size: "sm" })}
            >
              {hosted.nextCursor ? "See all" : "Open in the list"}
            </Link>
          </div>
          <ul className="divide-y rounded-md border">
            {hosted.liveBreaks.map((liveBreak) => {
              const state = liveBreakState(liveBreak);
              return (
                <li key={liveBreak.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <Link
                    to="/tenants/$tenantId/storefront/live-breaks/list/$liveBreakId"
                    params={{ tenantId, liveBreakId: liveBreak.id }}
                    className="min-w-0 flex-1 truncate text-sm hover:underline"
                  >
                    {liveBreak.title}
                  </Link>
                  <Badge variant={LIVE_BREAK_STATE_BADGE[state]}>
                    {LIVE_BREAK_STATE_LABELS[state]}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    <ShortDate value={liveBreak.createdAt} />
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="grid gap-4 sm:grid-cols-2">
        <div className="grid content-start gap-1">
          <h3 className="text-sm font-medium">Blocked from</h3>
          {first.blocks.against.length === 0 ? (
            <p className="text-sm text-muted-foreground">No live break blocks them.</p>
          ) : (
            <ul className="divide-y">
              {first.blocks.against.map((block) => (
                <BlockLine key={block.id} tenantId={tenantId} block={block} side="against" />
              ))}
            </ul>
          )}
        </div>
        <div className="grid content-start gap-1">
          <h3 className="text-sm font-medium">Blocks as a host</h3>
          {first.blocks.made.length === 0 ? (
            <p className="text-sm text-muted-foreground">They block no one.</p>
          ) : (
            <ul className="divide-y">
              {first.blocks.made.map((block) => (
                <BlockLine key={block.id} tenantId={tenantId} block={block} side="made" />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
